import { create } from 'zustand'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/integrations/supabase/client'
import { Logger } from '@/lib/utils/logger'

const logger = new Logger('AuthStore')

interface AuthState {
  user: User | null
  session: Session | null
  role: 'specialist' | 'caregiver' | null
  loading: boolean
  error: string | null
  initialized: boolean
  accessToken: string | null
  refreshToken: string | null
  userMetadata: Record<string, any> | null
  specialistId: string | null
  caregiverId: string | null
  signOut: () => Promise<void>
  clearError: () => void
  refreshAuth: () => Promise<void>
  setInitialized: (initialized: boolean) => void
  setAuthData: (session: Session | null) => void
  checkAuth: () => Promise<void>
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  role: null,
  loading: true,
  error: null,
  initialized: false,
  accessToken: null,
  refreshToken: null,
  userMetadata: null,
  specialistId: null,
  caregiverId: null,

  setAuthData: async (session: Session | null) => {
    logger.debug('Setting auth data', { hasSession: !!session })
    
    if (!session) {
      set({
        user: null,
        session: null,
        role: null,
        accessToken: null,
        refreshToken: null,
        userMetadata: null,
        specialistId: null,
        caregiverId: null,
        loading: false,
        initialized: true
      })
      return
    }

    let specialistId = null;
    let caregiverId = null;

    // If user is a specialist, fetch their specialist_id
    if (session.user.user_metadata?.role === 'specialist') {
      const { data: specialistData, error: specialistError } = await supabase
        .from('specialists')
        .select('id')
        .eq('auth_user_id', session.user.id)
        .maybeSingle()

      if (!specialistError && specialistData) {
        specialistId = specialistData.id;
      }
    }
    // If user is a caregiver, fetch their caregiver_id
    else if (session.user.user_metadata?.role === 'caregiver') {
      const { data: caregiverData, error: caregiverError } = await supabase
        .from('caregivers')
        .select('id')
        .eq('auth_user_id', session.user.id)
        .maybeSingle()

      if (!caregiverError && caregiverData) {
        caregiverId = caregiverData.id;
      }
    }

    set({
      user: session.user,
      session,
      role: session.user.user_metadata?.role || null,
      accessToken: session.access_token,
      refreshToken: session.refresh_token,
      userMetadata: session.user.user_metadata,
      specialistId,
      caregiverId,
      loading: false,
      initialized: true
    })
  },

  checkAuth: async () => {
    try {
      logger.debug('Checking auth state')
      set({ loading: true })
      
      const { data: { session } } = await supabase.auth.getSession()
      await get().setAuthData(session)
      
    } catch (error) {
      logger.error('Error checking auth state:', error)
      set({ 
        error: 'Failed to check authentication status',
        loading: false,
        initialized: true
      })
    }
  },

  signOut: async () => {
    try {
      set({ loading: true })
      await supabase.auth.signOut()
      get().setAuthData(null)
    } catch (error) {
      logger.error('Error signing out:', error)
      set({ error: 'Failed to sign out' })
    } finally {
      set({ loading: false })
    }
  },

  clearError: () => {
    set({ error: null })
  },

  refreshAuth: async () => {
    try {
      set({ loading: true })
      
      // First check if we have a current session
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session) {
        logger.debug('No session found during refresh')
        get().setAuthData(null)
        set({ loading: false })
        return
      }
      
      // Try to refresh the session
      const { data: { session: refreshedSession }, error } = await supabase.auth.refreshSession()
      
      if (error) {
        logger.error('Session refresh error:', error)
        
        // If refresh token is invalid, sign the user out
        if (error.message.includes('Invalid Refresh Token') || 
            error.message.includes('Token expired') ||
            error.message.includes('Token not found')) {
          logger.warn('Invalid refresh token detected - signing out user')
          await get().signOut() 
          set({ 
            error: 'Your session has expired. Please sign in again.',
            loading: false
          })
          return
        }
        
        throw error
      }

      logger.debug('Session successfully refreshed')
      await get().setAuthData(refreshedSession)
      set({ loading: false })
    } catch (error) {
      logger.error('Error refreshing auth:', error)
      set({ 
        error: 'Failed to refresh authentication',
        loading: false
      })
    }
  },

  setInitialized: (initialized: boolean) => {
    set({ initialized })
  }
}))
